import {
  DESK_VIEWS,
  DESK_VIEW_LABELS,
  LANE_LABELS,
  PRIORITY_LABELS,
  durationSince,
  laneLabel,
  laneWords,
  viewCount,
  type AskerPriority,
  type DeskView,
  type MySupportQuestion,
  type SupportKind,
  type SupportLane,
  type SupportQuestion,
  type SupportTurn,
} from '../lib/support'
import { useHubLang, dateLocaleFor } from '../lib/i18n'
import { HELP, HOME, SUPPORT } from '../lib/hubCopy'
import { ConeTecMark } from './icons'

/**
 * The support queue — HLP-3's inbox, rebuilt 26 Sep as the left half of the
 * desk (118), and cut down 11 Oct (174, the Support Desk Rework). The thread
 * it opens is `SupportThread`; this file is the list.
 *
 * ── IT IS IN components/, NOT IN THE ROUTE FILE ─────────────────────────────
 *
 * Importing a route pulls in AppShell, which imports an SVG the test bundler
 * cannot resolve — so a queue defined in `routes/Support.tsx` would be
 * unreachable by the web suite with every assertion passing against an empty
 * string. Everything here takes its data as props for the same reason: an
 * effect does not run under `renderToString`.
 *
 * ── THREE VIEWS, AND THE SERVER DECIDED WHICH ROW IS IN WHICH (174) ─────────
 *
 * Needs reply, Waiting on asker, Closed. The turn is `app.support_turn()`'s
 * and arrives on the row (118); the view is a FILTER the server applies before
 * the limit (`p_turn`, with `done` = closed or confirmed). Nothing here
 * re-derives a turn or sorts the page — a client sorting the page it was given
 * would show some of the matches and call it the queue (standing rule 1).
 *
 * Until 174 this was four chips ("Waiting on you / Waiting on them / Answered
 * / All") beside four other words for the same states. David could not find
 * how to finish a thread; one vocabulary, and a close the lane owns, is the
 * fix. The Closed view keeps "closed by the lane" and "confirmed by the asker"
 * apart on every row, so a clean queue cannot hide a question nobody
 * confirmed (090's objection, kept).
 *
 * ── A ROW CARRIES WHAT DECIDES WHETHER TO OPEN IT, AND NO MORE ──────────────
 *
 * Name, lane, age, two lines of the question, and at most these flags: the
 * asker's "not yet" (090's signal), High severity, the tracker id. Claude's
 * category, the picture and reply counts and the language moved to the thread
 * and its Details tray — eight badges on a 380px row was the "confusing" David
 * reported.
 *
 * ── EVERY ROW CARRIES ITS LANE, AND THE LABEL IS THE ASKER'S CHOICE ─────────
 *
 * David's call, 18 Sep (HLP-14): with one person holding all three lanes, the
 * door a question came through is the only routing observability there is.
 *
 * ── NO PER-PERSON COUNT, ANYWHERE ───────────────────────────────────────────
 *
 * The views count per turn; nothing counts by holder (B22, B09 §5, rule 8).
 */

/** What a lane chip says, and it is the ONE home for the wording (LANE_LABELS). */
function LaneChip({ lane }: { lane: SupportLane }) {
  return (
    <span className="pill qrow__lane" title={LANE_LABELS[lane]?.about}>
      {lane === 'platform' ? 'Platform' : lane === 'technical' ? 'Technical' : laneLabel(lane)}
    </span>
  )
}

/**
 * 348. The asker's priority on the desk (English, a staff screen). Can't work
 * is the one worth a colour; blocked is live; the other two are plain. The
 * title says whose word it is.
 */
export function PriorityPill({ priority, label }: { priority: AskerPriority; label?: string }) {
  const tone = priority === 'cant_work' ? ' pill--danger' : priority === 'blocked' ? ' pill--live' : ''
  return (
    <span className={`pill qrow__pri${tone}`} title="What the asker said when they sent it">
      {label ?? PRIORITY_LABELS[priority]}
    </span>
  )
}

/** The age line for a row, from the lane's side. Duration, not relative time,
 *  because it is the number being compared down the list. */
export function queueAge(q: SupportQuestion, now?: number): string {
  if (q.turn === 'holder') return `waiting ${durationSince(q.turnSince, now)}`
  if (q.turn === 'asker') return `you replied ${durationSince(q.turnSince, now)} ago`
  const day = new Date(q.turnSince).toLocaleDateString('en-CA', { day: 'numeric', month: 'short' })
  return q.turn === 'closed' ? `closed ${day}` : `confirmed ${day}`
}

export function SupportQueueRow({
  question: q,
  current = false,
  onOpen,
}: {
  question: SupportQuestion
  current?: boolean
  onOpen?: () => void
}) {
  const meta: React.ReactNode[] = []
  /* HLP-22. THE ASKER'S VERDICT, WHICH IS THE SIGNAL THIS QUEUE EXISTS TO
     RECEIVE. Stated as the QUESTION's state, never as a verdict on anybody's
     reply, and not `pill--danger`: a thread being open is normal (B22). */
  if (q.answeredTheAsker === false && q.turn !== 'closed') meta.push(<span className="pill" key="no">Not answered yet</span>)
  /* 174. In the Closed view the two kinds of done are told apart on the row. */
  if (q.turn === 'closed') meta.push(<span className="pill qrow__unconfirmed" key="cl">Not confirmed</span>)
  if (q.turn === 'answered') meta.push(<span className="pill pill--kind" key="ok">Confirmed</span>)
  /* 117's triage: High is the one severity worth a colour, and it is the
     danger colour because that is what it claims. */
  if (q.triageSeverity === 'high') meta.push(<span className="pill pill--danger" key="sev">High</span>)
  /* 348 (D4). The ASKER's word, beside the machine's severity and told apart
     from it: "Can't work" is what the person said, not what Claude read. */
  if (q.askerPriority) meta.push(<PriorityPill key="pri" priority={q.askerPriority} />)
  /* 117's handoff: the tracker id and the status last SEEN. */
  if (q.trackerIssueId) meta.push(<span className="qrow__ico qrow__dev" key="dev">{q.trackerIssueId}{q.trackerStatus ? ` · ${q.trackerStatus}` : ''}</span>)

  return (
    <li>
      <button
        type="button"
        className={`qrow qrow--${q.turn}${current ? ' qrow--on' : ''}`}
        aria-current={current ? 'true' : undefined}
        onClick={onOpen}
      >
        <span className="qrow__stripe" aria-hidden="true" />
        <span className="qrow__body">
          <span className="qrow__top">
            {/* PRIVATE MEANS PRIVATE TO A NAMED AUDIENCE, NOT ANONYMOUS (16 Sep). */}
            <span className="qrow__who">{q.askedBy ?? 'Somebody'}</span>
            <LaneChip lane={q.lane} />
            {q.kind === 'feedback' && <span className="pill qrow__lane">{HELP.en.feedbackTag}</span>}
            <span className="qrow__age">{queueAge(q)}</span>
          </span>
          <span className="qrow__q">
            {/* 123. The mark says One translated this question; the thread it
                opens offers the original. */}
            {q.translationStatus === 'machine' && <TranslatedMark />}
            {q.question}
          </span>
          {meta.length > 0 && <span className="qrow__meta">{meta}</span>}
        </span>
      </button>
    </li>
  )
}

export function SupportInbox({
  lanes,
  lane,
  onLane,
  kind = null,
  onKind,
  view,
  onView,
  counts,
  search = '',
  onSearch,
  rows,
  totalMatches,
  error,
  openQuestionId,
  onOpen,
}: {
  /** The lanes this person holds. One chip each, plus All when there is more
   *  than one — a filter over a list, which is what `.chip` means (D18). */
  lanes: readonly SupportLane[]
  lane: SupportLane | null
  onLane: (next: SupportLane | null) => void
  /** 337 (D3). Tickets, feedback, or both — filtered on the SERVER. Feedback
   *  only reaches the platform lane, so the filter shows only to its holders. */
  kind?: SupportKind | null
  onKind?: (next: SupportKind | null) => void
  /** 174. Which of the three views. The server filters before the limit. */
  view: DeskView
  onView: (next: DeskView) => void
  /** From `support_inbox_counts()` — true for the whole lane, not the page. */
  counts?: Record<SupportTurn, number> | null
  search?: string
  onSearch?: (next: string) => void
  rows: SupportQuestion[]
  totalMatches: number
  /**
   * A REFUSAL, rendered. `support_inbox()` raises rather than returning an
   * empty list for a lane you do not hold or when you hold none (rule 12).
   */
  error?: string | null
  openQuestionId?: string | null
  onOpen?: (id: string) => void
}) {
  if (error) {
    return (
      <div className="card">
        <p className="supportq__empty">{error}</p>
      </div>
    )
  }

  return (
    <div className="supportinbox">
      <div className="queue__tools">
        {/* 174. Three views, like mail folders: each is a place a thread IS,
            not a chip that narrows a list. Counts from the whole lane. */}
        <nav className="deskviews" aria-label="Views">
          {DESK_VIEWS.map((v) => (
            <button
              key={v}
              type="button"
              className={`deskview deskview--${v}${view === v ? ' deskview--on' : ''}`}
              aria-pressed={view === v}
              onClick={() => onView(v)}
            >
              <span className="deskview__dot" aria-hidden="true" />
              {DESK_VIEW_LABELS[v]}
              {counts && <span className="deskview__n">{viewCount(counts, v)}</span>}
            </button>
          ))}
        </nav>
        {lanes.length > 1 && (
          <div className="chips" role="group" aria-label="Lane">
            <button
              type="button"
              className={`chip chip--sm${lane === null ? ' chip--on' : ''}`}
              aria-pressed={lane === null}
              onClick={() => onLane(null)}
            >
              All lanes
            </button>
            {lanes.map((l) => (
              <button
                key={l}
                type="button"
                className={`chip chip--sm${lane === l ? ' chip--on' : ''}`}
                aria-pressed={lane === l}
                onClick={() => onLane(l)}
              >
                {laneLabel(l)}
              </button>
            ))}
          </div>
        )}
        {onKind && lanes.includes('platform') && (
          <div className="chips" role="group" aria-label={HELP.en.kindLabel}>
            {([null, 'question', 'feedback'] as const).map((k) => (
              <button
                key={k ?? 'all'}
                type="button"
                className={`chip chip--sm${kind === k ? ' chip--on' : ''}`}
                aria-pressed={kind === k}
                onClick={() => onKind(k)}
              >
                {k === null ? HELP.en.kindAll : k === 'question' ? HELP.en.kindQuestion : HELP.en.kindFeedback}
              </button>
            ))}
          </div>
        )}
        {onSearch && (
          <div>
            <label className="sr-only" htmlFor="queue-search">Search the queue</label>
            {/* The SERVER searches (118, p_search), so this is the whole view
                and not the page — standing rule 1. */}
            <input
              id="queue-search"
              className="field__input"
              type="search"
              placeholder="Search names and questions"
              value={search}
              onChange={(e) => onSearch(e.target.value)}
            />
          </div>
        )}
      </div>

      {rows.length === 0 ? (
        <p className="queue__empty">
          {view === 'holder'
            ? `Nothing needs a reply${lane ? ` in ${laneLabel(lane).toLowerCase()}` : ''}.`
            : view === 'asker'
              ? 'Nothing is waiting on an asker.'
              : 'Nothing closed yet.'}
        </p>
      ) : (
        <>
          <ul className="queue__list">
            {rows.map((q) => (
              <SupportQueueRow
                key={q.questionId}
                question={q}
                current={openQuestionId === q.questionId}
                onOpen={() => onOpen?.(q.questionId)}
              />
            ))}
          </ul>
          {/* REC-54: A LIMITED READ MUST SAY IT IS A PAGE. */}
          {totalMatches > rows.length && (
            <p className="supportinbox__page">
              Showing {rows.length} of {totalMatches}. {view === 'done' ? 'The newest are first.' : 'The longest-waiting are first.'}
            </p>
          )}
        </>
      )}
      <p className="queue__foot">
        <kbd>j</kbd><kbd>k</kbd> move · <kbd>r</kbd> reply · <kbd>n</kbd> note · <kbd>e</kbd> close · <kbd>/</kbd> search
      </p>
    </div>
  )
}

/**
 * YOUR OWN PRIVATE QUESTIONS — HLP-22.
 *
 * ── AN EMPTY LIST IS HONEST HERE, AND THAT IS NOT A CONTRADICTION ───────────
 *
 * `support_inbox()` REFUSES rather than returning nothing, because an empty
 * platform inbox reads as "nobody has asked anything" — a claim about the
 * company (rule 12). This list has the opposite property: "you have asked
 * nothing" is a fact about you, and the person reading it is the one authority
 * on whether it is true. So it returns a list and says the plain thing.
 *
 * ── AND IT NAMES THE LANE, BECAUSE THE LANE WAS THE PROMISE ─────────────────
 *
 * The ask surface told them who would read it before they typed (16 Sep). This
 * is the same sentence kept, later.
 *
 * ── THE TURN, FROM THIS SIDE (118) ──────────────────────────────────────────
 *
 * The same `app.support_turn()` the lane reads, worded from the asker's seat:
 * the lane's "waiting on you" is this person's "waiting on them". One
 * derivation, two vocabularies, so both people describe one thread the same
 * way.
 */
export function MyQuestions({
  rows,
  totalMatches,
  onOpen,
}: {
  rows: MySupportQuestion[]
  totalMatches: number
  onOpen: (questionId: string) => void
}) {
  const lang = useHubLang()
  const t = SUPPORT[lang]
  if (rows.length === 0) {
    return (
      <div className="card">
        <p className="supportq__empty">
          {t.noneAsked}
        </p>
      </div>
    )
  }

  return (
    <div className="supportinbox">
      {rows.map((q) => (
        <article className="card supportq" key={q.questionId}>
          <header className="supportq__head">
            {/* 337. Feedback is named as feedback - the asker sent an idea, not
                a question to a team (D3). Everything else names its lane. */}
            {q.kind === 'feedback' ? (
              <span className="pill">{HELP[lang].feedbackTag}</span>
            ) : (
              <span className="pill" title={laneWords(q.lane, lang).about}>
                {laneWords(q.lane, lang).label}
              </span>
            )}
            <time className="supportq__when" dateTime={q.askedAt}>
              {new Date(q.askedAt).toLocaleDateString(dateLocaleFor(lang) ?? 'en-CA')}
            </time>
            {/* Whose turn, from your side — and your own verdict reflected
                back. The only states here are ones you set or the other side's
                reply moved; nobody assigns you a status. */}
            {q.turn === 'answered' ? (
              <span className="pill pill--kind">{t.turn.answered}</span>
            ) : (
              /* 174. "Closed by support" is live like "They replied": both are
                 the asker's move — confirm, or say it is not solved. */
              <span className={`pill${q.turn === 'asker' || q.turn === 'closed' ? ' pill--live' : ''}`}>{t.turn[q.turn]}</span>
            )}
            {q.answeredTheAsker === false && <span className="pill">{t.stillOpen}</span>}
            {/* 348. What you said when you sent it - shown back, never a control. */}
            {q.askerPriority && (
              <span className="pill">{HELP[lang].youSaid(HELP[lang].priorities[q.askerPriority].label)}</span>
            )}
          </header>

          <h3 className="supportq__q">
            {q.translationStatus === 'machine' && <TranslatedMark />}
            {q.question}
          </h3>

          <footer className="supportq__foot">
            <button
              type="button"
              className="btn btn--quiet btn--sm"
              onClick={() => onOpen(q.questionId)}
            >
              {/* The count is the thing worth knowing at a glance: it is how
                  you tell an answered question from one still waiting, before
                  opening anything. */}
              {q.replies === 0 ? t.openNoReply : t.openReplies(q.replies)}
            </button>
          </footer>
        </article>
      ))}
      {/* REC-54: A LIMITED READ MUST SAY IT IS A PAGE. `p_limit` is applied by
          the database, so what is on screen is SOME of the matches presented as
          the answer unless this line is here. */}
      {totalMatches > rows.length && (
        <p className="supportinbox__page">
          {t.showing(rows.length, totalMatches)}
        </p>
      )}
    </div>
  )
}

/** 123. The mark alone, for a row with no room for the line (112's rail). */
function TranslatedMark() {
  const h = HOME[useHubLang()]
  return (
    <span className="wiki__translated" title={h.translatedTitle}>
      <ConeTecMark size={10} />
      <span className="sr-only">{h.translatedByOne}</span>
    </span>
  )
}
